/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useMemo } from 'react';
import { unstable_createMuiStrictModeTheme as createMuiTheme, MuiThemeProvider, jssPreset, StylesProvider } from '@material-ui/core';
import { BrowserRouter } from 'react-router-dom';
import { create } from 'jss';
import rtl from 'jss-rtl';
import { GlobalStyles, variables } from './styles';
import { AppRoute } from './AppRoute';
import { NotificationsProvider, useApiError, useAppData, useLoading } from './providers';
import { getTheme, getThemeByBrokerId } from './layouts';
import { getURLParam } from './utils';
import { ScrollToTop } from './components';

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

export const Theme: FC = () => {
  const { locale } = useAppData();
  const { saveLogoUrl, saveFooterLogoUrl, saveQRLogoUrl, authToken, removeToken, removeUserData, removeTranslations, removePartnershipList, removeCurrencyList } = useAppData();
  const { addError } = useApiError();
  const { setIsLoading } = useLoading();

  const brokerId = getURLParam('BrokerId') || process.env.REACT_APP_BROKER_ID;

  const theme = useMemo(
    () =>
      createMuiTheme({
        spacing: variables.spacing,
        palette: {
          text: {
            primary: variables.black50,
            secondary: variables.black,
          },
        },
        breakpoints: {
          values: {
            xl: variables.xl,
            lg: variables.lg,
            md: variables.md,
            sm: variables.sm,
            xs: variables.xs,
          },
        },
        overrides: {
          MuiButton: {
            root: {
              textTransform: 'none',
            },
            startIcon: {
              marginInlineStart: -4,
              marginInlineEnd: 8,
            },
          },
          MuiSvgIcon: {
            root: {
              'div[dir=rtl] &': {
                transform: 'scaleX(-1)',
              },
            },
          },
        },
        typography: {
          fontSize: 14,
          fontFamily: 'Inter',
          body1: {
            fontSize: 14,
          },
        },
      }),
    [],
  );

  useEffect(() => {
    if (locale && (locale === 'ar' || locale === 'fa')) {
      theme.direction = 'rtl';
      const body = document.getElementsByTagName('body');
      body[0].setAttribute('dir', 'rtl');
      theme.props = {
        MuiSelect: {
          MenuProps: {
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          },
        },
      };
    } else {
      theme.direction = 'ltr';
      const body = document.getElementsByTagName('body');
      body[0].setAttribute('dir', 'ltr');
      theme.props = {
        MuiSelect: {
          MenuProps: {
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
          },
        },
      };
    }
  }, [theme, locale]);

  useEffect(() => {
    if (brokerId || authToken) {
      (async () => {
        setIsLoading(true);
        try {
          let response;
          if (brokerId) {
            response = await getThemeByBrokerId(brokerId);
          }
          if (authToken) {
            response = await getTheme();
          }
          if (response) {
            const headlessCmsTheme = response.data;
            saveLogoUrl(headlessCmsTheme.logoUrl);
            saveFooterLogoUrl(headlessCmsTheme.footerLogo);
            saveQRLogoUrl(headlessCmsTheme.qrCodeLogo);
          }
        } catch (e) {
          if (e.response.status === 401) {
            removeTranslations();
            removePartnershipList();
            removeCurrencyList();
            removeToken();
            removeUserData();
          }
          addError(JSON.stringify(e.response));
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [saveLogoUrl, authToken, brokerId]);

  return (
    <StylesProvider jss={jss}>
      <BrowserRouter basename="/#">
        <MuiThemeProvider theme={theme}>
          <ScrollToTop>
            <NotificationsProvider>
              <GlobalStyles />
              <AppRoute />
            </NotificationsProvider>
          </ScrollToTop>
        </MuiThemeProvider>
      </BrowserRouter>
    </StylesProvider>
  );
};
